/**
 * ProductionMaster — PROD-1. The real SAP production master data (BOM / Master
 * Recipe / Production Version / Resource) + the approved-artwork register, in a
 * tabbed register. Route: /production. Bilingual (en-US / ar-SA).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PageHero } from '@edms/design-system';
import styles from '../Recalls/Recalls.module.css';
import { apiFetch } from '../../api/client';

interface MdRow { dataset: string; code: string | null; label: string | null; plant: string | null; row_no: number; row_data: Record<string, unknown> }
interface ArtworkRow {
  artwork_id: string; product: string; artwork_code: string | null; version: string | null;
  component_type: string | null; status: string; approved_at_utc: string | null;
  artwork_no: string | null; item_code: string | null; item_description: string | null;
  pharmacode: string | null; category: string | null; product_slug: string | null;
  file_object_key: string | null; file_name: string | null;
}
interface ArtworkFile {
  file_id: string; product: string; product_slug: string; file_name: string;
  object_key: string; component_hint: string | null; proof_state: string | null; size_bytes: number | null;
}
interface ProdResp {
  datasets: { BOM: MdRow[]; RECIPE: MdRow[]; PRODUCTION_VERSION: MdRow[]; RESOURCE: MdRow[] };
  counts: Record<string, number>;
  artwork: ArtworkRow[]; artwork_total: number;
  artwork_files: ArtworkFile[]; artwork_files_total: number;
}

const TABS = ['PRODUCTION_VERSION', 'BOM', 'RECIPE', 'RESOURCE', 'ARTWORK', 'ARTWORK_FILES'] as const;

// The artwork API is Bearer-auth, so fetch the presigned URL (with auth) then
// open it — a plain <a href> wouldn't carry the token.
async function openArtworkFile(objectKey: string) {
  try {
    const { url } = await apiFetch<{ url: string }>(`/api/v1/production/artwork/file?key=${encodeURIComponent(objectKey)}`);
    if (url) window.open(url, '_blank', 'noopener');
  } catch { /* surfaced by the query layer elsewhere; nothing actionable here */ }
}

export function ProductionMaster() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<(typeof TABS)[number]>('PRODUCTION_VERSION');
  const q = useQuery<ProdResp>({
    queryKey: ['production', 'master-data'],
    queryFn: ({ signal }) => apiFetch<ProdResp>('/api/v1/production/master-data', { signal }),
    staleTime: 60_000,
  });
  const tp = (k: string, opts?: Record<string, unknown>) => t(`production.${k}`, opts ?? {});
  const tabLabel = (k: string) => t(`production.tab.${k}`, { defaultValue: k });
  const count = (k: string) => (k === 'ARTWORK' ? q.data?.artwork_total ?? 0
    : k === 'ARTWORK_FILES' ? q.data?.artwork_files_total ?? 0
      : q.data?.counts?.[k] ?? 0);

  return (
    <main className={styles.page}>
      <PageHero eyebrow={tp('eyebrow')} title={tp('title')} subtitle={tp('subtitle')} />

      <div className={styles.filters} role="tablist" aria-label={tp('title')}>
        {TABS.map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k}
            className={`${styles.btn} ${tab === k ? styles.btnPrimary : styles.btnNeutral}`}
            onClick={() => setTab(k)}>
            {tabLabel(k)} <span className={styles.pill}>{count(k)}</span>
          </button>
        ))}
      </div>

      {q.isLoading ? <div className={styles.empty}>{tp('loading')}</div>
        : q.isError || !q.data ? <div className={styles.empty}>{tp('loadError')}</div>
          : tab === 'ARTWORK' ? (
            <table className={styles.table}>
              <thead><tr><th>{tp('thArtworkNo')}</th><th>{tp('thItemCode')}</th><th>{tp('thDescription')}</th><th>{tp('thComponent')}</th><th>{tp('thPharmacode')}</th><th>{tp('thFile')}</th><th>{tp('thStatus')}</th></tr></thead>
              <tbody>
                {q.data.artwork.map((a) => (
                  <tr key={a.artwork_id}>
                    <td className={styles.mono}>{a.artwork_no ?? a.artwork_code ?? '—'}</td>
                    <td className={styles.mono}>{a.item_code ?? '—'}</td>
                    <td>{a.item_description ?? a.product ?? '—'}</td>
                    <td>{a.component_type ? t(`production.artworkComponent.${a.component_type}`, { defaultValue: a.component_type }) : '—'}</td>
                    <td className={styles.mono}>{a.pharmacode ?? '—'}</td>
                    <td>{a.file_object_key
                      ? <button type="button" className={styles.linkBtn} onClick={() => openArtworkFile(a.file_object_key!)}>{tp('viewFile')}</button>
                      : '—'}</td>
                    <td><span className={`${styles.pill} ${styles.cls3}`}>{t(`production.artworkStatus.${a.status}`, { defaultValue: a.status })}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : tab === 'ARTWORK_FILES' ? (
            <table className={styles.table}>
              <thead><tr><th>{tp('thProduct')}</th><th>{tp('thFileName')}</th><th>{tp('thComponent')}</th><th>{tp('thProof')}</th><th>{tp('thFile')}</th></tr></thead>
              <tbody>
                {q.data.artwork_files.map((f) => (
                  <tr key={f.file_id}>
                    <td>{f.product}</td>
                    <td>{f.file_name}</td>
                    <td>{f.component_hint ? t(`production.artworkComponent.${f.component_hint}`, { defaultValue: f.component_hint }) : '—'}</td>
                    <td>{f.proof_state ? t(`production.artworkProof.${f.proof_state}`, { defaultValue: f.proof_state }) : '—'}</td>
                    <td><button type="button" className={styles.linkBtn} onClick={() => openArtworkFile(f.object_key)}>{tp('viewFile')}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className={styles.table}>
              <thead><tr><th>{tp('thCode')}</th><th>{tp('thLabel')}</th><th>{tp('thPlant')}</th></tr></thead>
              <tbody>
                {(q.data.datasets[tab as keyof ProdResp['datasets']] ?? []).map((r, i) => (
                  <tr key={i}>
                    <td className={styles.mono}>{r.code ?? '—'}</td>
                    <td>{r.label ?? '—'}</td>
                    <td>{r.plant ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
    </main>
  );
}
